import type { Schemas } from "@neio/contracts";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Database, RefreshCw } from "lucide-react";

import { apiGet, switchedOff } from "../../lib/http";
import { FeatureOff } from "../FeatureOff";
import { ErrorState, Loading } from "../States";

export type Health = Schemas["HealthView"];

/** `GET /admin/health`: the container's `/health` is not proxied to the browser. */
export function fetchHealth(): Promise<Health> {
  return apiGet("/api/v1/admin/health");
}

export function HealthView({ health, onRefresh }: { health: Health; onRefresh?: () => void }) {
  const ok = health.status === "ok";
  const n = health.problems.length;
  return (
    <div className="stack">
      <div className="tiles">
        <div className="card tile">
          <div className="l">Status</div>
          <div className="n"><span className={`pill ${ok ? "ok" : "review"}`}>{ok ? "ok" : "degraded"}</span></div>
        </div>
        <div className="card tile">
          <div className="l">Environment</div>
          <div className="n mono-lg">{health.environment}</div>
        </div>
        <div className="card tile">
          <div className="l">Database</div>
          <div className="n">
            <span className={`pill ${health.database === "ok" ? "ok" : "fail"}`}>
              <Database size={12} aria-hidden="true" /> {health.database === "ok" ? "Reachable" : "Database not reachable"}
            </span>
          </div>
        </div>
      </div>
      {n === 0 ? (
        <div className="state-box" role="status">
          <CheckCircle2 aria-hidden="true" />
          <div className="state-title">No problems reported</div>
        </div>
      ) : (
        <div className="card">
          <div className="section-title row"><AlertTriangle size={16} aria-hidden="true" /> {n} {n === 1 ? "problem" : "problems"}</div>
          <ul className="problem-list">
            {health.problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <p className="muted">Each is a deployment setting to fix on the host. Passwords in a connection string are hidden.</p>
        </div>
      )}
      {onRefresh && (
        <div><button className="ghost" onClick={onRefresh}><RefreshCw size={14} aria-hidden="true" /> Check again</button></div>
      )}
    </div>
  );
}

export function HealthTab() {
  const query = useQuery({ queryKey: ["admin", "health"], queryFn: fetchHealth });
  const off = switchedOff(query.error, [404]);
  if (query.isPending) return <Loading what="Asking the API how it is" />;
  if (off !== null) return <FeatureOff feature="Admin health" reason={off} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  return <HealthView health={query.data} onRefresh={() => query.refetch()} />;
}
